import LOCATION_TYPE from '../components/enum/location_type';
import connectionType from '../components/common/connection-type';

const getAuthToken = () => {
  const user = JSON.parse(sessionStorage.getItem('user') || 'null');
  return user && user.signInUserSession && user.signInUserSession.idToken
    ? user.signInUserSession.idToken.jwtToken
    : '';
};

const licensingHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

const licensingFetch = async (path, options = {}) => {
  const response = await fetch(`${process.env.REACT_APP_BASEURL}${path}`, {
    withCredentials: true,
    ...options,
    headers: {
      ...licensingHeaders(),
      ...(options.headers || {}),
    },
  });
  if (response.status === 204) {
    return null;
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || data.ErrorMessage || 'Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

export const OTC_DELIVERY_KEY = 'otc_delivery';
export const COUNTER_KEY = 'counter';
export const CURBSIDE_KEY = 'curbside';
export const BEDSIDE_KEY = 'bedside';
export const COURIER_KEY = 'courier';
export const SHOP_KEY = 'kiosk_marketplace';
export const CUSTOM_BRAND_MESSAGING_KEY = 'custom_brand_messaging';
export const CAMPAIGNS_KEY = 'campaigns';

export const COMING_SOON_FEATURE_KEYS = [
  SHOP_KEY,
  COURIER_KEY,
  CUSTOM_BRAND_MESSAGING_KEY,
  CAMPAIGNS_KEY,
];

export const isComingSoonFeature = (featureKey, title) =>
  COMING_SOON_FEATURE_KEYS.indexOf(featureKey) !== -1 ||
  String(title || '').trim().toLowerCase() === 'shop';

export const displaySolutionTitle = (featureKey, title) => {
  if (featureKey === CUSTOM_BRAND_MESSAGING_KEY) {
    return 'Studio';
  }
  return title || '';
};
export const OTC_FEATURE_PATH = `/home/features/${OTC_DELIVERY_KEY}`;
export const PRODUCT_LICENSING_PATH = '/home/manage/product-licensing';
export const CONSOLE_PATH = '/home/manage/console';
export const NON_RX_CATALOG_PATH = '/home/manage/non-rx-catalog';
export const MESSAGE_CONTROL_PATH = '/home/manage/message-control-center';
export const EMAIL_TEMPLATES_PATH = '/home/manage/templates/email';
export const SMS_TEMPLATES_PATH = '/home/manage/templates/sms';
export const LINK_MESSAGES_PATH = '/home/manage/templates/mobile';
export const STAFF_TEMPLATES_PATH = '/home/manage/templates/staff';
export const CAMPAIGNS_PATH = '/home/manage/campaigns';
export const CAMPAIGNS_LIST_PATH = '/home/manage/campaigns/list';
export const CAMPAIGNS_ANALYTICS_PATH = '/home/manage/campaigns/analytics';
export const TEMPLATES_PATH_PREFIX = '/home/manage/templates';
export const ASSIGN_PATH = '/home/prescription/assign';
export const UNPLUGGED_ASSIGN_PATH = '/home/prescription/unpluggedassign';

export const LOCATION_TYPE_FEATURE_KEYS = {
  [LOCATION_TYPE.Counter]: COUNTER_KEY,
  [LOCATION_TYPE.Curbside]: CURBSIDE_KEY,
  [LOCATION_TYPE.Bedside]: BEDSIDE_KEY,
  [LOCATION_TYPE.Courier]: COURIER_KEY,
};

export const MANAGE_LOCATION_FEATURE_KEYS = {
  Counter: COUNTER_KEY,
  Curbside: CURBSIDE_KEY,
  Bedside: BEDSIDE_KEY,
  Courier: COURIER_KEY,
};

export const CONSOLE_TYPE_FEATURE_KEYS = {
  counter: COUNTER_KEY,
  curbside: CURBSIDE_KEY,
  bedside: BEDSIDE_KEY,
  courier: COURIER_KEY,
};

export const FEATURE_TYPE_LOCATION_IDS = {
  counter: LOCATION_TYPE.Counter,
  curbside: LOCATION_TYPE.Curbside,
  bedside: LOCATION_TYPE.Bedside,
  courier: LOCATION_TYPE.Courier,
};

export const featureKeyForLocationType = (locationTypeId) => {
  const normalized = String(locationTypeId || '').trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  return (
    LOCATION_TYPE_FEATURE_KEYS[locationTypeId] ||
    LOCATION_TYPE_FEATURE_KEYS[normalized] ||
    null
  );
};

export const featureKeyForManageLocationName = (value) =>
  MANAGE_LOCATION_FEATURE_KEYS[value] || null;

export const featureKeyForConsoleType = (type) =>
  CONSOLE_TYPE_FEATURE_KEYS[String(type || '').trim().toLowerCase()] || null;

export const LOCATION_TYPE_MESSAGING_METHODS = {
  [LOCATION_TYPE.Counter]: 'counter',
  [LOCATION_TYPE.Curbside]: 'curbside',
  [LOCATION_TYPE.Bedside]: 'bedside',
  [LOCATION_TYPE.Courier]: 'courier',
  [LOCATION_TYPE.Kiosk]: 'kiosk',
  [LOCATION_TYPE.Mail_Order]: 'mail_order',
  [LOCATION_TYPE.Other]: 'other',
};

export const messagingMethodForLocationType = (locationTypeId) => {
  const normalized = String(locationTypeId || '').trim().toLowerCase();
  return (
    LOCATION_TYPE_MESSAGING_METHODS[locationTypeId] ||
    LOCATION_TYPE_MESSAGING_METHODS[normalized] ||
    null
  );
};

export const PROMOTED_PRODUCTS = [
  {
    feature_key: OTC_DELIVERY_KEY,
    labelKey: 'Leftbar.NonRxCatalog',
    defaultLabel: 'Marketplace',
    tooltip:
      'iLocal Marketplace — Prescriptions and more, delivered to your patients.',
    enabledPath: NON_RX_CATALOG_PATH,
    requiresConsole: false,
    lockedHintKey: 'NonRxCatalog.LockedHint',
    lockedHintDefault: 'Open this to enable Marketplace.',
  },
  {
    feature_key: COUNTER_KEY,
    labelKey: 'Leftbar.Counter',
    defaultLabel: 'Counter',
    tooltip:
      'Counter pickup — complete the order at the pharmacy window with the same pickup code.',
    enabledPath: `${ASSIGN_PATH}?type=counter`,
    consoleType: 'counter',
    requiresConsole: true,
    lockedHintKey: 'Products.LockedHint.Counter',
    lockedHintDefault: 'Open this to enable Counter.',
  },
  {
    feature_key: CURBSIDE_KEY,
    labelKey: 'Leftbar.Curbside',
    defaultLabel: 'Curbside',
    tooltip:
      'Curbside pickup — bring the order to the patient waiting in their vehicle.',
    enabledPath: `${ASSIGN_PATH}?type=curbside`,
    consoleType: 'curbside',
    requiresConsole: true,
    lockedHintKey: 'Products.LockedHint.Curbside',
    lockedHintDefault: 'Open this to enable Curbside.',
  },
  {
    feature_key: BEDSIDE_KEY,
    labelKey: 'Leftbar.Bedside',
    defaultLabel: 'Bedside',
    tooltip:
      'Bedside pickup — complete the order at the patient room with the same pickup code.',
    enabledPath: `${ASSIGN_PATH}?type=bedside`,
    consoleType: 'bedside',
    requiresConsole: true,
    lockedHintKey: 'Products.LockedHint.Bedside',
    lockedHintDefault: 'Open this to enable Bedside.',
  },
  {
    feature_key: SHOP_KEY,
    labelKey: 'Leftbar.Shop',
    defaultLabel: 'Shop',
    tooltip:
      'Sell Marketplace items from reserved kiosk bins. Shop, pay, and open the door.',
    enabledPath: '/home/features/kiosk_marketplace',
    requiresConsole: false,
    lockedHintKey: 'Products.LockedHint.Shop',
    lockedHintDefault: 'Open this to enable Shop.',
  },
  {
    feature_key: COURIER_KEY,
    labelKey: 'Leftbar.Courier',
    defaultLabel: 'Courier',
    tooltip:
      'Courier delivery — send the filled order out for delivery with the same pickup code.',
    enabledPath: `${ASSIGN_PATH}?type=courier`,
    consoleType: 'courier',
    requiresConsole: true,
    lockedHintKey: 'Products.LockedHint.Courier',
    lockedHintDefault: 'Open this to enable Courier.',
  },
  {
    feature_key: CUSTOM_BRAND_MESSAGING_KEY,
    labelKey: 'Leftbar.Studio',
    defaultLabel: 'Studio',
    tooltip:
      'Control SMS, email, and mobile-link copy patients receive for each delivery method.',
    enabledPath: EMAIL_TEMPLATES_PATH,
    requiresConsole: false,
    lockedHintKey: 'Products.LockedHint.CustomBrandMessaging',
    lockedHintDefault: 'Open this to enable Studio.',
  },
  {
    feature_key: CAMPAIGNS_KEY,
    labelKey: 'Leftbar.Campaigns',
    defaultLabel: 'Campaigns',
    tooltip:
      'One-off and bulk promotional SMS and email: one patient, all patients, or by zip.',
    enabledPath: CAMPAIGNS_LIST_PATH,
    requiresConsole: false,
    lockedHintKey: 'Products.LockedHint.Campaigns',
    lockedHintDefault: 'Open this to enable Campaigns.',
  },
];

export const getFeatureLockedRedirectPath = (featureKey) =>
  `/home/features/${featureKey}`;

export const isPromotedFeaturePath = (pathname) => {
  const path = String(pathname || '').replace(/\/$/, '');
  return PROMOTED_PRODUCTS.some(
    (product) => path === getFeatureLockedRedirectPath(product.feature_key)
  );
};

export const getConsoleTypeFromSearch = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    const type = String(params.get('type') || '')
      .trim()
      .toLowerCase();
    if (['counter', 'curbside', 'bedside', 'courier'].indexOf(type) !== -1) {
      return type;
    }
  } catch (_err) {
    return '';
  }
  return '';
};

export const getLocationTypeIdFromSearch = (search) => {
  const type = getConsoleTypeFromSearch(search);
  return FEATURE_TYPE_LOCATION_IDS[type] || '';
};

export const getDeliverPrescriptionPath = () => {
  try {
    const accountConnectionId = sessionStorage.getItem('accountConnectionId');
    if (accountConnectionId === connectionType.UNPLUGGED) {
      return UNPLUGGED_ASSIGN_PATH;
    }
  } catch (_err) {
    // fall through to the standard assign screen
  }
  return ASSIGN_PATH;
};

export const isDeliverPrescriptionPath = (pathname) => {
  const path = String(pathname || '').replace(/\/$/, '');
  return (
    path === ASSIGN_PATH ||
    path === UNPLUGGED_ASSIGN_PATH ||
    path.indexOf('/prescription/assign') !== -1 ||
    path.indexOf('/prescription/unpluggedassign') !== -1
  );
};

export const getInitialAssignLocationTypeId = (search) => {
  const fromUrl = getLocationTypeIdFromSearch(search);
  if (fromUrl) {
    try {
      localStorage.setItem('selectedLocationTypeId', fromUrl);
    } catch (_err) {
      // ignore storage failures and still return the URL type
    }
    return fromUrl;
  }
  try {
    return localStorage.getItem('selectedLocationTypeId') || '';
  } catch (_err) {
    return '';
  }
};

export const getLockedPromotedEntitlement = (featureKey) => {
  const product = PROMOTED_PRODUCTS.find(
    (item) => item.feature_key === featureKey
  );
  if (!product) {
    return null;
  }
  return {
    feature_key: product.feature_key,
    title: product.defaultLabel,
    tooltip: product.tooltip || product.defaultLabel,
    enabled: false,
    reason: 'locked',
    talk_to_expert: true,
  };
};

export const getProductLicensingPath = (options) => {
  const accountId = options && options.accountId;
  const featureKey = options && options.featureKey;
  const params = new URLSearchParams();
  if (accountId) params.set('accountId', accountId);
  if (featureKey) params.set('feature', featureKey);
  const query = params.toString();
  return query ? `${PRODUCT_LICENSING_PATH}?${query}` : PRODUCT_LICENSING_PATH;
};

export const getLicensingQueryAccountId = () => {
  try {
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    return params.get('accountId') || params.get('account_id') || '';
  } catch (_err) {
    return '';
  }
};

export const getLicensingQueryFeatureKey = () => {
  try {
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    return params.get('feature') || '';
  } catch (_err) {
    return '';
  }
};

export const LOCKED_OTC_DELIVERY_ENTITLEMENT = {
  feature_key: OTC_DELIVERY_KEY,
  title: 'Marketplace',
  tooltip:
    'iLocal Marketplace — Prescriptions and more, delivered to your patients.',
  enabled: false,
  reason: 'locked',
  talk_to_expert: true,
};

export const resolveOtcDeliveryEntitlement = (entitlements) =>
  (Array.isArray(entitlements) ? entitlements : []).find(
    (item) => item.feature_key === OTC_DELIVERY_KEY
  ) || LOCKED_OTC_DELIVERY_ENTITLEMENT;

export const isIlocalBoxUser = () => {
  try {
    return localStorage.getItem('isIlocalUser') === 'true';
  } catch (_err) {
    return false;
  }
};

export const fetchFeatures = () => licensingFetch('api/v1/features');

export const getSelectedAccountId = () => {
  try {
    const accountId = sessionStorage.getItem('parentAccountId');
    if (!accountId || accountId === 'null' || accountId === 'undefined') {
      return undefined;
    }
    return accountId;
  } catch (_err) {
    return undefined;
  }
};

const isEnabledFlag = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const ENTITLEMENTS_TTL_MS = 30000;
const entitlementsCache = {};
const entitlementsInflight = {};

export const fetchEntitlements = ({ boxId, accountId } = {}) => {
  const params = new URLSearchParams();
  const resolvedBoxId = boxId || getSelectedBoxId();
  const resolvedAccountId = accountId || getSelectedAccountId();
  if (resolvedBoxId) params.set('box_id', resolvedBoxId);
  if (resolvedAccountId) params.set('account_id', resolvedAccountId);
  const query = params.toString();
  const cacheKey = query || 'default';
  const hit = entitlementsCache[cacheKey];
  if (hit && Date.now() - hit.at < ENTITLEMENTS_TTL_MS) {
    return Promise.resolve(hit.value);
  }
  if (entitlementsInflight[cacheKey]) {
    return entitlementsInflight[cacheKey];
  }
  entitlementsInflight[cacheKey] = licensingFetch(
    `api/v1/entitlements${query ? `?${query}` : ''}`
  )
    .then((data) => {
      entitlementsCache[cacheKey] = { at: Date.now(), value: data };
      entitlementsInflight[cacheKey] = null;
      return data;
    })
    .catch((err) => {
      entitlementsInflight[cacheKey] = null;
      throw err;
    });
  return entitlementsInflight[cacheKey];
};

export const fetchFeatureLocations = () =>
  licensingFetch('api/v1/feature-locations');

export const submitFeatureLead = (payload) =>
  licensingFetch('api/v1/feature-leads', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const recordFeatureEvent = (payload) =>
  licensingFetch('api/v1/feature-events', {
    method: 'POST',
    body: JSON.stringify(payload),
  }).catch(() => null);

export const searchLicensingAccounts = (search) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  return licensingFetch(`api/v1/admin/accounts?${params.toString()}`);
};

export const fetchAdminEntitlements = (accountId) =>
  licensingFetch(
    `api/v1/admin/entitlements?account_id=${encodeURIComponent(accountId)}`
  );

export const setAdminEntitlement = (payload) =>
  licensingFetch('api/v1/admin/entitlements', {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((data) => {
    Object.keys(entitlementsCache).forEach((key) => {
      delete entitlementsCache[key];
    });
    return data;
  });

export const fetchFeaturePurchases = ({ search, featureKey, status } = {}) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (featureKey) params.set('feature_key', featureKey);
  if (status) params.set('status', status);
  const query = params.toString();
  return licensingFetch(
    `api/v1/admin/feature-purchases${query ? `?${query}` : ''}`
  );
};

export const fetchAdminFeatureLeads = ({
  search,
  featureKey,
  status,
  accountId,
} = {}) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (featureKey) params.set('feature_key', featureKey);
  if (status) params.set('status', status);
  if (accountId) params.set('account_id', accountId);
  const query = params.toString();
  return licensingFetch(
    `api/v1/admin/feature-leads${query ? `?${query}` : ''}`
  );
};

export const fetchAdminEnablementHistory = ({
  search,
  featureKey,
  action,
  accountId,
} = {}) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (featureKey) params.set('feature_key', featureKey);
  if (action) params.set('action', action);
  if (accountId) params.set('account_id', accountId);
  const query = params.toString();
  return licensingFetch(
    `api/v1/admin/feature-enablement-history${query ? `?${query}` : ''}`
  );
};

export const fetchAdminCatalog = () =>
  licensingFetch('api/v1/admin/feature-catalog');

export const createAdminCatalogFeature = (payload) =>
  licensingFetch('api/v1/admin/feature-catalog', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminCatalogFeature = (featureKey, payload) =>
  licensingFetch(
    `api/v1/admin/feature-catalog/${encodeURIComponent(featureKey)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );

export const deleteAdminCatalogFeature = (featureKey) =>
  licensingFetch(
    `api/v1/admin/feature-catalog/${encodeURIComponent(featureKey)}`,
    {
      method: 'DELETE',
    }
  );

export const uploadFeatureCatalogImage = async (file) => {
  const data = new FormData();
  data.append('file', file);
  const response = await fetch(
    `${process.env.REACT_APP_BASEURL}api/v1/admin/feature-catalog/upload-image`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: data,
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.message || 'Unable to upload that image.');
  }
  return result.url || result.location;
};

export const findEntitlement = (entitlements, featureKey) =>
  (Array.isArray(entitlements) ? entitlements : []).find(
    (item) => item.feature_key === featureKey
  ) || null;

export const isFeatureUsable = (entitlement) =>
  !!(
    entitlement &&
    isEnabledFlag(entitlement.enabled) &&
    entitlement.reason !== 'geo_blocked'
  );

export const isLocationTypeUsable = (locationTypeId, entitlements) => {
  const featureKey = featureKeyForLocationType(locationTypeId);
  if (!featureKey) {
    return true;
  }
  if (!Array.isArray(entitlements)) {
    return true;
  }
  return isFeatureUsable(findEntitlement(entitlements, featureKey));
};

export const isManageLocationTypeUsable = (value, entitlements) => {
  const featureKey = featureKeyForManageLocationName(value);
  if (!featureKey) {
    return true;
  }
  if (!Array.isArray(entitlements)) {
    return true;
  }
  return isFeatureUsable(findEntitlement(entitlements, featureKey));
};

export const isConsoleTypeUsable = (type, entitlements) => {
  const featureKey = featureKeyForConsoleType(type);
  if (!featureKey) {
    return true;
  }
  if (!Array.isArray(entitlements)) {
    return true;
  }
  return isFeatureUsable(findEntitlement(entitlements, featureKey));
};

export const decorateLocationTypesWithLicense = (locationTypes, entitlements) =>
  (Array.isArray(locationTypes) ? locationTypes : []).map((type) => {
    const featureKey = featureKeyForLocationType(type && type.id);
    const pending = !!featureKey && !Array.isArray(entitlements);
    return {
      ...type,
      featureKey,
      pending,
      locked:
        !pending && featureKey
          ? !isLocationTypeUsable(type.id, entitlements)
          : false,
    };
  });

export const getFirstUsableLocationTypeId = (
  locationTypes,
  entitlements,
  preferredId
) => {
  const types = Array.isArray(locationTypes) ? locationTypes : [];
  if (
    preferredId &&
    types.some((type) => type && type.id === preferredId) &&
    isLocationTypeUsable(preferredId, entitlements)
  ) {
    return preferredId;
  }
  const kiosk = types.find((type) => type && type.id === LOCATION_TYPE.Kiosk);
  if (kiosk) {
    return kiosk.id;
  }
  const firstUsable = types.find(
    (type) => type && isLocationTypeUsable(type.id, entitlements)
  );
  return (firstUsable && firstUsable.id) || '';
};

export const getSelectedBoxId = () => {
  try {
    const boxId = localStorage.getItem('selectedLocation');
    if (!boxId || boxId === 'null' || boxId === 'undefined') {
      return undefined;
    }
    return boxId;
  } catch (_err) {
    return undefined;
  }
};

export const fetchOtcDeliveryEntitlement = async () => {
  try {
    const data = await fetchEntitlements({ boxId: getSelectedBoxId() });
    return resolveOtcDeliveryEntitlement(data && data.entitlements);
  } catch (_err) {
    return LOCKED_OTC_DELIVERY_ENTITLEMENT;
  }
};

export const fetchOtcDeliveryEnabled = async () =>
  isFeatureUsable(await fetchOtcDeliveryEntitlement());

export const getOtcLockedRedirectPath = () =>
  getFeatureLockedRedirectPath(OTC_DELIVERY_KEY);

export const isOtcFeaturePath = (pathname) => {
  const path = String(pathname || '').replace(/\/$/, '');
  return path === getFeatureLockedRedirectPath(OTC_DELIVERY_KEY);
};

export const isPromotedEntitlementPending = (entitlement) =>
  !!(entitlement && entitlement.reason === 'pending');

export const getPromotedProductNavPath = (product, entitlement) => {
  if (
    isPromotedEntitlementPending(entitlement) ||
    isFeatureUsable(entitlement)
  ) {
    if (product && product.consoleType) {
      return getDeliverPrescriptionPath() + '?type=' + product.consoleType;
    }
    return product.enabledPath;
  }
  return getFeatureLockedRedirectPath(product.feature_key);
};

export const resolvePromotedEntitlement = (entitlements, featureKey) => {
  if (!Array.isArray(entitlements)) {
    const product = PROMOTED_PRODUCTS.find(
      (item) => item.feature_key === featureKey
    );
    return {
      feature_key: featureKey,
      title: product ? product.defaultLabel : featureKey,
      tooltip: product ? product.tooltip || product.defaultLabel : featureKey,
      enabled: false,
      reason: 'pending',
    };
  }
  return (
    findEntitlement(entitlements, featureKey) ||
    getLockedPromotedEntitlement(featureKey)
  );
};

export const isFeatureGeoBlocked = (entitlement) =>
  !!(entitlement && entitlement.reason === 'geo_blocked');

export const isFeatureVisibleToAccount = (entitlement) =>
  !!(
    entitlement &&
    (entitlement.enabled === true ||
      entitlement.reason === 'geo_blocked' ||
      (entitlement.reason === 'locked' && entitlement.talk_to_expert !== false))
  );

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value) => UUID_PATTERN.test(String(value || '').trim());

export const displaySafeText = (value, fallback = '') => {
  const text = String(value == null ? '' : value).trim();
  if (!text || isUuid(text)) {
    return fallback;
  }
  return text;
};

export const getSessionContactEmail = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    const payload =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.payload;
    const candidates = [
      payload && payload.email,
      user && user.attributes && user.attributes.email,
      payload && payload['cognito:username'],
      user && user.username,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const value = String(candidates[i] || '').trim();
      if (value && value.includes('@') && !isUuid(value)) {
        return value;
      }
    }
  } catch (_err) {
    return '';
  }
  return '';
};

export const featureTitleForKey = (featureKey, entitlements) => {
  const match = findEntitlement(entitlements, featureKey);
  if (match && match.title) {
    return match.title;
  }
  return displaySafeText(featureKey, '');
};



// WEBPACK FOOTER //
// ./src/utils/productLicensing.js